import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Group : famille "ensemble nommé de contacts".
 *
 * Ensemble nommé de contacts géré comme un tout : équipe, foyer, section,
 * classe, segment ou comité, pour inscrire, facturer ou convoquer en une seule
 * opération. Construite sur le modèle "famille + type" de la bible (patron :
 * `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète ("équipe soignante",
 * "foyer Martin", "classe CP") : il connaît la famille générique `group`. Le
 * vocabulaire métier est porté par le champ `type` (chaîne libre déclarée par
 * l'app), qui est le `kind` de la fiche renommé selon la convention du socle
 * (ex. "equipe", "foyer", "segment", "classe", "comite"). Un group est un
 * ensemble NOMMÉ : `name` est donc requis (comme resource / worker / product).
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma) :
 * `memberContactIds` liste les membres (références `contact`), `ownerWorkerId`
 * le responsable interne (référence `worker`), `payerContactId` le contact
 * facturé pour le groupe (référence `contact`). Une facture vise toujours un
 * contact PAYEUR désigné, jamais le group directement (la "facturation commune
 * du foyer" regroupe les lignes sur ce payeur). Les liens inverses (une
 * `registration` inscrite au nom du groupe, une `campaign` qui le cible) vivent
 * côté enfant : c'est l'autre entité qui porte le `groupId`, le group ne les
 * stocke pas.
 *
 * Accès : v0 reste org-wide (même doctrine que asset). Rien de sensible signalé
 * sur cette famille.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille group (hors champs standard du socle). */
export const groupCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "equipe", "foyer", "classe"). C'est le `kind` de la fiche. */
  type: z.string().min(1),
  /** Nom d'affichage du groupe (ex. "Équipe A", "Foyer Martin", "Classe CP"). */
  name: z.string().min(1),
  /** Membres du groupe (références `contact`), optionnel. */
  memberContactIds: z.array(z.string()).optional(),
  /** Responsable interne du groupe (référence `worker`), optionnel. */
  ownerWorkerId: z.string().min(1).optional(),
  /** Capacité maximale (nombre de membres), optionnel. */
  capacity: z.number().int().optional(),
  /** Contact facturé pour le groupe (référence `contact`), optionnel. */
  payerContactId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille group (champs standard + champs métier). */
export const GroupSchema = z.object({
  ...baseEntityFields,
  ...groupCoreFields,
});

/** Type d'un group. */
export type Group = z.infer<typeof GroupSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const GroupCreateInputSchema = z.object({
  ...groupCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un group. */
export type GroupCreateInput = z.infer<typeof GroupCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont gérés par le SDK : aucun des deux n'est accepté ici.
 */
export const GroupUpdateInputSchema = z
  .object({ ...groupCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise à jour d'un group. */
export type GroupUpdateInput = z.infer<typeof GroupUpdateInputSchema>;
